// src/components/VisitorList.tsx

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

//const API_URL = 'http://172.16.20.112:3000/api/visitors'; // 로컬 서버 주소
const API_URL = 'https://rek-hybrid.co.kr/api/visitors'; // 실제 서버 주소

// 1. Visitor 데이터 타입 정의
interface Visitor {
  num: number;
  name: string;
  company: string;
  grade: string;
  mobile: string;
  mail: string;
}

// 2. Props 타입 정의 (MainPage에서 전달받음)
interface VisitorListProps {
    onEdit: (num: number) => void; // 수정 버튼 클릭 시 부모로 num을 전달하는 함수
    refreshTrigger: number; // 새로고침을 위한 트리거
}

function VisitorList({ onEdit, refreshTrigger }: VisitorListProps): React.JSX.Element {
  // 3. State에 타입 적용: useState<Visitor[]>
  const [visitors, setVisitors] = useState<Visitor[]>([]); 
  const [searchKeyword, setSearchKeyword] = useState('');
  
  // 방문자 목록 불러오기 (keyword에 타입 적용)
  const fetchVisitors = useCallback(async (keyword: string = '') => {
    try {
      // 응답 데이터 타입 지정
      const response = await axios.get<Visitor[]>(API_URL, { 
        params: { keyword: keyword }
      });
      setVisitors(response.data);
    } catch (error) {
      console.error("방문자 목록 로딩 실패:", error);
      alert("목록을 불러오는 데 실패했습니다.");
    }
  }, []);

  useEffect(() => {
    // refreshTrigger가 변경될 때마다 목록 새로고침
    fetchVisitors(searchKeyword);
  }, [fetchVisitors, refreshTrigger, searchKeyword]); // searchKeyword도 의존성 배열에 추가

  // 검색 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchVisitors(searchKeyword);
  };
  
  // 4. 수정 버튼 클릭 핸들러 (num에 타입 적용)
  const handleEditClick = (num: number) => {
    onEdit(num); 
  };

  return (
    <div className="visitor-list-container">
      
      {/* 검색 영역 */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title text-muted">방문자 검색</h5>
          
          {/* Input Group을 사용하여 검색창과 버튼을 깔끔하게 배치 */}
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="이름, 회사, 이메일로 검색"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }} // 엔터키 검색 기능 추가
            />
            <button className="btn btn-outline-secondary" type="button" onClick={handleSearch}>
              <i className="bi bi-search me-1"></i> {/* 아이콘 사용 시 */}
              검색
            </button>
          </div>
        </div>
      </div>
      
      {/* 목록 영역 */}
      <div className="card shadow-sm">
        <div className="card-header bg-light">
           <h5 className="mb-0">방문자 목록 <span className="badge bg-primary rounded-pill">{visitors.length}</span></h5>
        </div>
        <div className="card-body p-0">
            {visitors.length === 0 ? (
                <div className="text-center p-5 text-muted">
                    검색 결과가 없습니다.
                </div>
            ) : (
                // 테이블을 반응형으로 만들어 작은 화면에서도 가로 스크롤 가능하게 처리
                <div className="table-responsive">
                    <table className="table table-striped table-hover mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th>No.</th>
                                <th>이름</th>
                                <th>회사명</th>
                                <th>직급</th>
                                <th>연락처</th>
                                <th>이메일</th>
                                <th>액션</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visitors.map((visitor) => (
                                <tr key={visitor.num}>
                                    <td>{visitor.num}</td>
                                    <td>{visitor.name}</td>
                                    <td>{visitor.company}</td>
                                    <td>{visitor.grade}</td>
                                    <td>{visitor.mobile}</td>
                                    <td>{visitor.mail}</td>
                                    <td>
                                        <button 
                                            onClick={() => handleEditClick(visitor.num)}
                                            className="btn btn-xl btn-warning" // 버튼 디자인 개선
                                        >
                                            수정
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default VisitorList;